import { useState, useEffect, useCallback, memo, useRef } from 'react';
import { SegmentFilterSheet, AudienceUsersTable } from '@/components/audience-segment';
import type { SegmentDefinition } from '@masters-club/validation';

interface TabRecipientsProps {
  /** Начальный сегмент (для edit mode populate). */
  initialDefinition: SegmentDefinition;
  initialSelectedUserIds: number[];
  /** Ref-callback для синхронизации с parent (без ререндера). */
  onDataSync: (data: {
    definition: SegmentDefinition;
    selectedUserIds: number[];
    recipientCount?: number | null;
  }) => void;
  isPending: boolean;
}

/**
 * Вкладка «Получатели» рассылки: конструктор сегмента + полная таблица
 * пользователей (как в разделе «Пользователи»). Выбранные строки
 * добавляются к рассылке поверх сегмента (OR) через selectedUserIds.
 */
export const TabRecipients = memo(function TabRecipients({
  initialDefinition,
  initialSelectedUserIds,
  onDataSync,
  isPending,
}: TabRecipientsProps) {
  const [definition, setDefinition] = useState<SegmentDefinition>(initialDefinition);
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>(initialSelectedUserIds);
  const [recipientCount, setRecipientCount] = useState<number | null>(null);

  const syncRef = useRef(onDataSync);
  syncRef.current = onDataSync;

  useEffect(() => {
    syncRef.current({ definition, selectedUserIds, recipientCount });
  }, [definition, selectedUserIds, recipientCount]);

  const handleSelectedChange = useCallback((ids: number[]) => {
    setSelectedUserIds(ids);
  }, []);

  const handleTotalChange = useCallback((total: number) => {
    setRecipientCount(total);
  }, []);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <SegmentFilterSheet
          value={definition}
          onChange={setDefinition}
          disabled={isPending}
          mode="live"
          triggerLabel="Условия отбора"
          title="Условия отбора получателей"
          description="Настройте сегмент аудитории. Без условий рассылку получат все пользователи."
        />
        {selectedUserIds.length > 0 && (
          <p className="text-xs text-muted-foreground">
            Выбранные пользователи получат рассылку дополнительно к условиям сегмента (OR).
          </p>
        )}
      </div>

      <AudienceUsersTable
        previewUrl="/audience/preview"
        definition={definition}
        disabled={isPending}
        onTotalChange={handleTotalChange}
        onSelectedChange={handleSelectedChange}
      />
    </div>
  );
});
